import React, { useState, useMemo } from "react";
import { THEME, flexxState, awardXP } from "./state";
import { DIAGNOSTIC_QUESTIONS, scoreDiagnostic, TRACK_LABEL, IDK_INDEX, IDK_LABEL } from "./diagnosticData";
import KnowledgeRadar from "./KnowledgeRadar";
import Portal from "./Portal";

// Full diagnostic flow: 15 questions → score → profile screen
export default function DiagnosticQuiz({ onComplete }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [finished, setFinished] = useState(null);

  // Defensive bypass — if the diagnostic flag is already true (e.g. seeded by
  // a testing agent, or restored mid-render from cloud sync), short-circuit
  // the modal and call onComplete on the next tick so the parent advances.
  // Always runs as a hook (no conditional hook ordering).
  const alreadyDone = flexxState.isDiagnosticDone();
  React.useEffect(() => {
    if (alreadyDone) {
      const t = setTimeout(() => onComplete?.(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [alreadyDone, onComplete]);
  if (alreadyDone) return null;

  const q = DIAGNOSTIC_QUESTIONS[idx];
  const total = DIAGNOSTIC_QUESTIONS.length;
  const progressPct = Math.round(((idx + (locked ? 1 : 0)) / total) * 100);

  const choose = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const nextAnswers = { ...answers, [q.id]: i };
    setAnswers(nextAnswers);
    setTimeout(() => {
      if (idx + 1 >= total) {
        const result = scoreDiagnostic(nextAnswers);
        flexxState.saveDiagnosticScore(result.total);
        flexxState.saveTrack(result.track);
        flexxState.saveGraph({
          marketStructure: result.perCategory.marketStructure,
          ta: result.perCategory.ta,
          risk: result.perCategory.risk,
          psychology: result.perCategory.psychology,
          strategy: result.perCategory.strategy,
        });
        flexxState.setDiagnosticDone(true);
        awardXP(100, "Completed diagnostic");
        setFinished(result);
      } else {
        setIdx(idx + 1);
        setSelected(null);
        setLocked(false);
      }
    }, 750);
  };

  if (finished) {
    return <DiagnosticResult result={finished} onContinue={onComplete} />;
  }

  return (
    <Portal>
    <div data-testid="flexx-diagnostic-modal" style={overlay}>
      <div className="flexx-slide-up" style={container}>
        <header style={headerRow}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>FLEXX DIAGNOSTIC GATEWAY</div>
            <h3 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 20 }}>Question {idx + 1} / {total}</h3>
          </div>
          <div className="flexx-chip gold" data-testid="diagnostic-category-chip">{CATEGORY_LABEL[q.category]}</div>
        </header>

        <div className="flexx-progress-bar" style={{ margin: "18px 0 22px" }}>
          <span style={{ width: `${progressPct}%` }} />
        </div>

        <p data-testid={`diagnostic-question-${idx}`} style={{ color: THEME.text, fontSize: 16, lineHeight: 1.55, marginBottom: 18 }}>
          {q.q}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...q.choices, IDK_LABEL].map((c, i) => {
            const isIdk = i === IDK_INDEX;
            const isSel = selected === i;
            // IDK is a neutral choice — no green/red feedback, no shake.
            // Real answer choices keep the standard correct/wrong feedback.
            const isCorrect = !isIdk && locked && i === q.correct;
            const isWrong = !isIdk && locked && isSel && i !== q.correct;
            const isIdkSelected = isIdk && locked && isSel;
            const cls = [
              "flexx-quiz-option",
              isSel && !locked ? "selected" : "",
              isCorrect ? "correct" : "",
              isWrong ? "wrong flexx-shake" : "",
              isIdk ? "idk" : "",
            ].join(" ");
            return (
              <button
                key={i}
                data-testid={`diagnostic-option-${idx}-${i}`}
                disabled={locked}
                className={cls}
                onClick={() => choose(i)}
                style={isIdk ? {
                  marginTop: 4,
                  opacity: 0.85,
                  borderStyle: isIdkSelected ? "solid" : "dashed",
                  borderColor: isIdkSelected ? THEME.gold + "80" : THEME.border,
                  fontStyle: "italic",
                } : undefined}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: "50%",
                  border: `1px solid ${isIdk ? THEME.mute : THEME.border}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, color: isIdk ? THEME.mute : THEME.mute,
                }}>
                  {isIdk ? "?" : String.fromCharCode(65 + i)}
                </span>
                <span>{c}</span>
                {isIdkSelected && (
                  <span data-testid={`diagnostic-idk-ack-${idx}`} style={{ marginLeft: "auto", fontSize: 10, color: THEME.gold, fontFamily: "monospace", letterSpacing: 1 }}>
                    HONEST · NOTED
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 18, fontSize: 11, color: THEME.mute, textAlign: "center" }}>
          This 15-question diagnostic assigns your personalized learning track.
        </div>
      </div>
    </div>
    </Portal>
  );
}

function DiagnosticResult({ result, onContinue }) {
  const track = TRACK_LABEL[result.track];
  const graph = useMemo(
    () => ({
      marketStructure: result.perCategory.marketStructure,
      ta: result.perCategory.ta,
      risk: result.perCategory.risk,
      psychology: result.perCategory.psychology,
      strategy: result.perCategory.strategy,
    }),
    [result]
  );

  return (
    <Portal>
    <div data-testid="flexx-diagnostic-result" style={overlay}>
      <div className="flexx-slide-up" style={{ ...container, maxWidth: 640 }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>YOUR LEARNING PROFILE</div>
          <h2 style={{ color: THEME.text, fontSize: 26, margin: "6px 0 10px" }}>
            Score: <span style={{ color: THEME.gold }}>{result.total}%</span>
          </h2>
          <div className="flexx-chip" style={{ borderColor: `${track.color}80`, background: `${track.color}20`, color: track.color }} data-testid="diagnostic-track-badge">
            {track.name}
          </div>
          <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6, marginTop: 12 }}>{track.blurb}</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
          <KnowledgeRadar scores={graph} size={280} />
        </div>

        <div style={{ background: "#13161c", border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 14, marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: THEME.tealSoft, border: `1px solid ${THEME.tealEdge}`, display: "flex", alignItems: "center", justifyContent: "center", color: THEME.teal, fontWeight: 800 }}>FX</div>
            <div style={{ color: THEME.gold, fontSize: 11, letterSpacing: 1, fontFamily: "monospace" }}>AXEL</div>
          </div>
          <p style={{ color: THEME.text, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {personalizedMessage(result)}
          </p>
        </div>

        <button
          data-testid="diagnostic-continue-btn"
          className="flexx-btn-primary"
          style={{ width: "100%", marginTop: 18 }}
          onClick={() => onContinue && onContinue(result)}
        >
          Enter the Academy →
        </button>
      </div>
    </div>
    </Portal>
  );
}

const CATEGORY_LABEL = {
  marketStructure: "Market Structure",
  ta: "Technical Analysis",
  risk: "Risk Awareness",
  psychology: "Psychology",
  strategy: "Strategy",
};

function personalizedMessage(result) {
  const weakest = Object.entries(result.perCategory).sort((a, b) => a[1] - b[1])[0];
  const label = CATEGORY_LABEL[weakest[0]];
  const idkPart = result.idkCount > 0
    ? ` You marked ${result.idkCount} question${result.idkCount === 1 ? "" : "s"} as "I don't know" — that's the most useful signal you can give me. Honesty over guessing wins every time.`
    : "";
  if (result.total >= 76) {
    return `Strong baseline. You're already past the basics — I've routed you into the Advanced track. Weakest area: ${label} (${weakest[1]}%). We'll reinforce it with targeted reviews.${idkPart}`;
  }
  if (result.total >= 41) {
    return `Solid fundamentals. I've placed you on the Intermediate track so you can skip the basics and go straight to execution. Weakest area: ${label}. Spaced repetition will tighten this up.${idkPart}`;
  }
  return `No shame in starting from zero — that's actually an edge. Foundational track locked in. Weakest area: ${label}. We'll build from the ground up, one mastery gate at a time.${idkPart}`;
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9800,
  background: "rgba(0,0,0,0.88)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

const container = {
  background: THEME.surface,
  border: `1px solid ${THEME.border}`,
  borderRadius: 18, padding: 28,
  maxWidth: 560, width: "100%",
  maxHeight: "92vh", overflowY: "auto",
};

const headerRow = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
};
